export type McpConnectorSource = 'custom' | 'json' | 'url';
export type McpConnectorAuthType = 'NONE' | 'BEARER' | 'API_KEY_HEADER';
export type McpConnectorTransportType = 'STREAMABLE_HTTP';

export interface McpHeaderInput {
    name: string;
    value: string;
}

export interface CreateMcpConnectorRequest {
    displayName: string;
    serverUrl: string;
    transportType: McpConnectorTransportType;
    authType: McpConnectorAuthType;
    authHeaderName?: string;
}

export interface McpCredentialRequest {
    authType: McpConnectorAuthType;
    secret?: string;
    apiKeyHeaderName?: string;
}

export interface NormalizeMcpConnectorInputParams {
    source: McpConnectorSource;
    displayName: string;
    serverUrl: string;
    headers?: McpHeaderInput[];
}

export interface NormalizedMcpConnectorInput {
    source: McpConnectorSource;
    createRequest: CreateMcpConnectorRequest;
    credentialRequest: McpCredentialRequest | null;
}

export interface McpOAuthDraftInputParams {
    displayName: string;
    serverUrl: string;
}

export interface McpManualCredentialInput {
    authType: McpConnectorAuthType;
    secret?: string;
    apiKeyHeaderName?: string;
}

export interface UnsupportedMcpImport {
    name: string;
    reason: string;
}

export interface McpJsonImportResult {
    connectors: NormalizedMcpConnectorInput[];
    unsupported: UnsupportedMcpImport[];
}

const BACKEND_TRANSPORT_TYPE: McpConnectorTransportType = 'STREAMABLE_HTTP';
const MCP_SERVERS_KEY = 'mcpServers';
const STDIO_UNSUPPORTED_REASON = 'STDIO MCP servers are not supported in P0 admin configuration.';

const trimToNull = (value?: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    return trimmed || null;
};

const requireNonBlank = (value: string, fieldName: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
        throw new Error(`${fieldName} is required`);
    }
    return trimmed;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
    !!value && typeof value === 'object' && !Array.isArray(value)
);

const normalizeHeaderInputs = (headers: McpHeaderInput[] = []): McpHeaderInput[] => (
    headers
        .map(header => ({
            name: header.name.trim(),
            value: header.value.trim(),
        }))
        .filter(header => header.name && header.value)
);

const assertSupportedHeaderName = (headerName: string) => {
    if (!/^[A-Za-z0-9-]{1,128}$/.test(headerName)) {
        throw new Error('Header name must contain only letters, numbers, and hyphens.');
    }
};

const buildCredentialFromHeaders = (headers: McpHeaderInput[]): McpCredentialRequest | null => {
    const normalizedHeaders = normalizeHeaderInputs(headers);

    if (normalizedHeaders.length === 0) {
        return null;
    }

    if (normalizedHeaders.length > 1) {
        throw new Error('Only one custom header is supported in P0 admin configuration.');
    }

    const [header] = normalizedHeaders;
    assertSupportedHeaderName(header.name);

    if (header.name.toLowerCase() === 'authorization') {
        const bearerMatch = header.value.match(/^Bearer\s+(.+)$/i);
        if (bearerMatch?.[1]?.trim()) {
            return {
                authType: 'BEARER',
                secret: bearerMatch[1].trim(),
            };
        }
    }

    return {
        authType: 'API_KEY_HEADER',
        secret: header.value,
        apiKeyHeaderName: header.name,
    };
};

export const normalizeMcpConnectorInput = ({
    source,
    displayName,
    serverUrl,
    headers = [],
}: NormalizeMcpConnectorInputParams): NormalizedMcpConnectorInput => {
    const normalizedDisplayName = requireNonBlank(displayName, 'displayName');
    const normalizedServerUrl = requireNonBlank(serverUrl, 'serverUrl');
    const credentialRequest = buildCredentialFromHeaders(headers);
    const authType = credentialRequest?.authType || 'NONE';
    const createRequest: CreateMcpConnectorRequest = {
        displayName: normalizedDisplayName,
        serverUrl: normalizedServerUrl,
        transportType: BACKEND_TRANSPORT_TYPE,
        authType,
    };

    if (credentialRequest?.authType === 'API_KEY_HEADER' && credentialRequest.apiKeyHeaderName) {
        createRequest.authHeaderName = credentialRequest.apiKeyHeaderName;
    }

    return {
        source,
        createRequest,
        credentialRequest,
    };
};

export const buildMcpCredentialRequest = (
    input: NormalizedMcpConnectorInput
): McpCredentialRequest | null => input.credentialRequest;

export const buildMcpOAuthDraftInput = ({
    displayName,
    serverUrl,
}: McpOAuthDraftInputParams): NormalizedMcpConnectorInput => (
    normalizeMcpConnectorInput({
        source: 'url',
        displayName,
        serverUrl,
    })
);

export const buildMcpManualCredentialRequest = ({
    authType,
    secret,
    apiKeyHeaderName,
}: McpManualCredentialInput): McpCredentialRequest | null => {
    if (authType === 'NONE') {
        return null;
    }

    const normalizedSecret = requireNonBlank(secret || '', 'secret');
    if (authType === 'BEARER') {
        return { authType, secret: normalizedSecret };
    }

    if (authType === 'API_KEY_HEADER') {
        const normalizedHeaderName = requireNonBlank(apiKeyHeaderName || '', 'apiKeyHeaderName');
        assertSupportedHeaderName(normalizedHeaderName);
        return {
            authType,
            secret: normalizedSecret,
            apiKeyHeaderName: normalizedHeaderName,
        };
    }

    throw new Error('Manual OAuth credentials are not supported.');
};

const stripJsonComments = (value: string): string => {
    let result = '';
    let inString = false;
    let quote = '';
    let escaped = false;

    for (let index = 0; index < value.length; index += 1) {
        const char = value[index];
        const next = value[index + 1];

        if (inString) {
            result += char;
            if (escaped) {
                escaped = false;
            } else if (char === '\\') {
                escaped = true;
            } else if (char === quote) {
                inString = false;
            }
            continue;
        }

        if (char === '"' || char === "'") {
            inString = true;
            quote = char;
            result += char;
            continue;
        }

        if (char === '/' && next === '/') {
            while (index < value.length && value[index] !== '\n') {
                index += 1;
            }
            result += '\n';
            continue;
        }

        if (char === '/' && next === '*') {
            index += 2;
            while (index < value.length && !(value[index] === '*' && value[index + 1] === '/')) {
                index += 1;
            }
            index += 1;
            continue;
        }

        result += char;
    }

    return result;
};

const parseJsonObject = (rawJson: string): Record<string, unknown> => {
    const parsed = JSON.parse(stripJsonComments(rawJson));
    if (!isPlainObject(parsed)) {
        throw new Error('MCP JSON import must be an object.');
    }
    return parsed;
};

const getServerEntries = (root: Record<string, unknown>): Array<[string, Record<string, unknown>]> => {
    const mcpServers = root[MCP_SERVERS_KEY];
    if (isPlainObject(mcpServers)) {
        return Object.entries(mcpServers)
            .filter((entry): entry is [string, Record<string, unknown>] => isPlainObject(entry[1]));
    }

    if (trimToNull(root.url) || trimToNull(root.serverUrl)) {
        return [[trimToNull(root.name) || 'Custom MCP', root]];
    }

    return [];
};

const extractServerUrl = (serverConfig: Record<string, unknown>): string | null => (
    trimToNull(serverConfig.url)
        || trimToNull(serverConfig.serverUrl)
        || trimToNull(serverConfig.server_url)
        || trimToNull(serverConfig.sseUrl)
        || trimToNull(serverConfig.httpUrl)
);

const extractHeaders = (serverConfig: Record<string, unknown>): McpHeaderInput[] => {
    const headers = serverConfig.headers;
    if (!isPlainObject(headers)) {
        return [];
    }

    return Object.entries(headers)
        .map(([name, value]) => ({
            name,
            value: typeof value === 'string' ? value : String(value ?? ''),
        }));
};

export const parseMcpJsonImport = (rawJson: string): McpJsonImportResult => {
    const root = parseJsonObject(rawJson);
    const connectors: NormalizedMcpConnectorInput[] = [];
    const unsupported: UnsupportedMcpImport[] = [];
    const entries = getServerEntries(root);

    entries.forEach(([entryName, serverConfig]) => {
        const displayName = trimToNull(serverConfig.name) || entryName;
        const serverUrl = extractServerUrl(serverConfig);

        if (!serverUrl) {
            unsupported.push({
                name: displayName,
                reason: trimToNull(serverConfig.command) ? STDIO_UNSUPPORTED_REASON : 'MCP server URL is required.',
            });
            return;
        }

        try {
            connectors.push(normalizeMcpConnectorInput({
                source: 'json',
                displayName,
                serverUrl,
                headers: extractHeaders(serverConfig),
            }));
        } catch (error) {
            unsupported.push({
                name: displayName,
                reason: error instanceof Error ? error.message : 'Invalid MCP server configuration.',
            });
        }
    });

    if (entries.length === 0) {
        unsupported.push({
            name: 'MCP JSON',
            reason: 'No MCP server entries found. Expected an object with mcpServers.',
        });
    }

    return {
        connectors,
        unsupported,
    };
};
